// src/components/collaboration/CollaborationRail.jsx
import React, { useEffect } from "react";
import { useAppViewStore } from "../../stores/useAppViewStore";
import { useAuthStore } from "../../stores/useAuthStore";
import { useCollabRailStore, COLLAB_RAIL_WIDTH } from "../../stores/useCollabRailStore";
import { useCollaborationStore } from "../../stores/useCollaborationStore";
import { useProjectStore } from "../../stores/useProjectStore";
import { useQcStore } from "../../stores/useQcStore";
import { useAppNav } from "../../hooks/useAppNav";
import CollaborationTimeline from "./CollaborationTimeline";

function resolveProjectKey(view) {
  if (!view || !view.tab) return "global";
  if (view.tab === "product_detail") {
    return view.detail?.id ? `product:${String(view.detail.id)}` : "product:unknown";
  }
  if (view.tab === "task_point")  return "task:point";
  if (view.tab === "task_linear") return "task:linear";
  if (view.tab === "task_area")   return "task:area";
  return String(view.tab);
}

export default function CollaborationRail() {
  const { view }             = useAppViewStore();
  const { open }             = useCollabRailStore();
  const orgId                = useAuthStore((s) => s.orgId);
  const orgName              = useAuthStore((s) => s.orgName ?? null);
  const contextScope         = useCollaborationStore((s) => s.contextScope);
  const setScope             = useCollaborationStore((s) => s.setScope);
  const threads              = useCollaborationStore((s) => s.threads);

  // D2.4 — case scope wiring. When a PadRekko case is loaded into
  // useQcStore, the rail surfaces a third scope option ("Case") and
  // can show case-specific events (case_created, case_stage_transition).
  //
  // D3 — pill visibility expanded: shows whenever the user has any
  // cases (casesList non-empty), not just when a case is open. Click
  // when no case is open → navigate to PadRekko picker.
  const caseInfo  = useQcStore((s) => s.caseInfo);
  const casesList = useQcStore((s) => s.casesList);
  const caseKey   = caseInfo ? `case:${caseInfo.id}` : null;
  const caseName  = caseInfo?.name ?? null;
  const caseCount = Array.isArray(casesList) ? casesList.length : 0;
  const casePillVisible = caseCount > 0 || caseKey !== null;

  const nav = useAppNav();
  const handleOpenPicker = () => nav.goMapTab("padrekko");

  // C1 — in PadRekko with a concrete project selected, the "Project" scope is
  // that project's rollup thread (project:<id>); the backend expands it to the
  // project's participated case threads. Elsewhere it stays the product/tab key.
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const projectKey =
    view?.tab === "padrekko" && activeProjectId && activeProjectId !== "global"
      ? `project:${activeProjectId}`
      : resolveProjectKey(view);
  const orgKey     = orgId ? `org:${orgId}` : "org";
  const contextKey =
    contextScope === "case" && caseKey ? caseKey :
    contextScope === "org"              ? orgKey :
                                          projectKey;

  const projectUnread = (threads[projectKey]?.events || []).some((e) => e.unread);
  const orgUnread     = (threads[orgKey]?.events     || []).some((e) => e.unread);
  const caseUnread    = caseKey
    ? (threads[caseKey]?.events || []).some((e) => e.unread)
    : false;

  // Auto-switch away from "case" scope when the case becomes unavailable
  // (user navigates out of the case in QC). v0 falls back to "project".
  // TODO(v1 polish): if users complain about the context jump, track
  // lastNonCaseScope and restore that instead of hardcoding "project".
  useEffect(() => {
    if (contextScope === "case" && !caseKey) {
      setScope("project");
    }
  }, [contextScope, caseKey, setScope]);

  if (view?.tab === "intent") return null;

  return (
    <aside
      style={{
        width: open ? COLLAB_RAIL_WIDTH : 0,
        flexShrink: 0,
        overflow: "hidden",
      }}
      className="relative h-full border-l border-zinc-800 transition-[width] duration-300 ease-in-out"
    >
      {/* Keep mounted so content doesn't flash on open */}
      <div style={{ width: COLLAB_RAIL_WIDTH }} className="h-full">
        <CollaborationTimeline
          contextKey={contextKey}
          projectKey={projectKey}
          orgKey={orgKey}
          caseKey={caseKey}
          caseName={caseName}
          caseAvailable={caseKey !== null}
          casePillVisible={casePillVisible}
          caseCount={caseCount}
          onOpenPicker={handleOpenPicker}
          projectUnread={projectUnread}
          orgUnread={orgUnread}
          caseUnread={caseUnread}
          orgName={orgName}
        />
      </div>
    </aside>
  );
}
